import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAppointments } from '@/api/hooks/appointments.hooks'
import {
  AppointmentStatus,
  AppointmentPaymentStatus,
  ApprovalFeeOutcome,
  getAppointmentStatusLabel,
  getApprovalFeeOutcomeLabel,
  getPaymentStatusLabel,
  getDateRangeParams,
  DATE_RANGE_LABELS,
} from '@/api/enums'
import { AdminPageLayout } from '../components/AdminPageLayout'
import { AdminPageSkeleton } from '../components/AdminPageSkeleton'
import { FilterBar } from './components/FilterBar'
import { ActiveFiltersList } from './components/ActiveFiltersList'
import { AppointmentsTable } from './components/AppointmentsTable'

export function AdminDashboardLayout() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('all')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [feeOutcomeFilter, setFeeOutcomeFilter] = useState('all')
  const [dateRangeFilter, setDateRangeFilter] = useState('all')

  const params = {
    ...(statusFilter !== 'all' && { status: Number(statusFilter) as AppointmentStatus }),
    ...(paymentFilter !== 'all' && { paymentStatus: Number(paymentFilter) as AppointmentPaymentStatus }),
    ...(feeOutcomeFilter !== 'all' && { approvalFeeOutcome: Number(feeOutcomeFilter) as ApprovalFeeOutcome }),
    ...(dateRangeFilter !== 'all' && getDateRangeParams(dateRangeFilter)),
  }

  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAppointments(params)

  const appointments = data?.pages.flat() ?? []

  const activeFilters = [
    statusFilter !== 'all' && {
      label: getAppointmentStatusLabel(Number(statusFilter) as AppointmentStatus),
      onRemove: () => setStatusFilter('all'),
    },
    paymentFilter !== 'all' && {
      label: getPaymentStatusLabel(Number(paymentFilter) as AppointmentPaymentStatus),
      onRemove: () => setPaymentFilter('all'),
    },
    feeOutcomeFilter !== 'all' && {
      label: getApprovalFeeOutcomeLabel(Number(feeOutcomeFilter) as ApprovalFeeOutcome),
      onRemove: () => setFeeOutcomeFilter('all'),
    },
    dateRangeFilter !== 'all' && {
      label: DATE_RANGE_LABELS[dateRangeFilter] ?? dateRangeFilter,
      onRemove: () => setDateRangeFilter('all'),
    },
  ].filter(Boolean) as { label: string; onRemove: () => void }[]

  if (isLoading) return <AdminPageSkeleton variant='dashboard' />

  return (
    <AdminPageLayout title='Admin Dashboard' subtitle='Manage appointment requests and bookings'>
      <section className='space-y-4'>
        <FilterBar
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          paymentFilter={paymentFilter}
          setPaymentFilter={setPaymentFilter}
          feeOutcomeFilter={feeOutcomeFilter}
          setFeeOutcomeFilter={setFeeOutcomeFilter}
          dateRangeFilter={dateRangeFilter}
          setDateRangeFilter={setDateRangeFilter}
          activeFilters={activeFilters}
          clearAllFilters={() => { setStatusFilter('all'); setPaymentFilter('all'); setFeeOutcomeFilter('all'); setDateRangeFilter('all') }}
        />
        <ActiveFiltersList activeFilters={activeFilters} />
        <AppointmentsTable
          appointments={appointments}
          onRowClick={(id) => navigate({ to: '/admin/appointment/$id', params: { id } })}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </section>
    </AdminPageLayout>
  )
}
